import { useEffect, useRef } from 'react';
import { GameMode, GameState, GamePhase } from '../types';

/* ───────────────────────────────────────────────────────────────
 *  Module-level singleton Audio engine.
 *  Lives outside React — never destroyed by re-renders/unmounts.
 * ─────────────────────────────────────────────────────────────── */

const _audio = new Audio();
_audio.preload = 'auto';

let _wantSrc = '';
let _wantLoop = false;

function canPlayOggOpus() {
  try {
    return _audio.canPlayType('audio/ogg; codecs=opus') !== '';
  } catch {
    return false;
  }
}

function pickTrack(oggPath: string, fallbackMp3Path: string) {
  return canPlayOggOpus() ? oggPath : fallbackMp3Path;
}

const BATTLE_TRACKS = [
  pickTrack('/OST/battle0.ogg', '/OST/battle0.mp3'),
  pickTrack('/OST/battle1.ogg', '/OST/battle1.mp3'),
  pickTrack('/OST/battle2.ogg', '/OST/battle2.mp3'),
  pickTrack('/OST/battle3.ogg', '/OST/battle3.mp3'),
  pickTrack('/OST/battle4.ogg', '/OST/battle4.mp3'),
  pickTrack('/OST/battle5.ogg', '/OST/battle5.mp3'),
];
const MENU_TRACK = pickTrack('/OST/Main%20Menu.ogg', '/OST/Main%20Menu.mp3');
const WIN_TRACK  = pickTrack('/OST/Win.ogg', '/OST/Win.MP3');
const LOSE_TRACK = pickTrack('/OST/loose.ogg', '/OST/loose.mp3');

type MusicContext = 'MENU' | 'BATTLE' | 'WIN' | 'LOSE' | 'NONE';

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ───────────────────────────────────────────────────────────────
 *  Public API: call this on every click inside the App.
 *  If the wanted track is already playing → does nothing.
 *  If it's paused/stopped → starts it.
 * ─────────────────────────────────────────────────────────────── */
export function ensureMusicPlaying() {
  if (!_wantSrc) return;
  if (_audio.src.endsWith(_wantSrc.replace(/%20/g, ' ')) && !_audio.paused) return;
  if (_audio.src.endsWith(_wantSrc) && !_audio.paused) return;

  _audio.src = _wantSrc;
  _audio.loop = _wantLoop;
  _audio.play().catch(() => {});
}

/* ───────────────────────────────────────────────────────────────
 *  React component — reads props, sets _wantSrc/_wantLoop,
 *  and manages battle playlist. Does NOT render any DOM.
 * ─────────────────────────────────────────────────────────────── */

interface AudioPlayerProps {
  gameMode: GameMode;
  gameState: GameState | null;
  localPlayerId: number;
  musicVolume: number; // 0–100
  globalMute?: boolean;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  gameMode,
  gameState,
  localPlayerId,
  musicVolume,
  globalMute = false,
}) => {
  const prevContextRef = useRef<MusicContext>('NONE');
  const battlePlaylist = useRef<string[]>([]);
  const battleIndex = useRef(0);

  // ── Determine desired music context ────────────────────────
  const desiredContext: MusicContext = (() => {
    if (gameMode === 'PLAYING' && gameState) {
      if (gameState.phase === GamePhase.GameOver) {
        return gameState.winner?.id === localPlayerId ? 'WIN' : 'LOSE';
      }
      return 'BATTLE';
    }
    return 'MENU';
  })();

  // ── When context changes, switch the wanted track ──────────
  useEffect(() => {
    if (desiredContext === prevContextRef.current) return;
    prevContextRef.current = desiredContext;

    switch (desiredContext) {
      case 'MENU':
        _wantSrc = MENU_TRACK;
        _wantLoop = true;
        break;

      case 'BATTLE': {
        const list = shuffleArray(BATTLE_TRACKS);
        battlePlaylist.current = list;
        battleIndex.current = 0;
        _wantSrc = list[0];
        _wantLoop = false;
        break;
      }

      case 'WIN':
        _wantSrc = WIN_TRACK;
        _wantLoop = false;
        break;

      case 'LOSE':
        _wantSrc = LOSE_TRACK;
        _wantLoop = false;
        break;

      default:
        _wantSrc = '';
        _wantLoop = false;
        _audio.pause();
        _audio.src = '';
        return;
    }

    // Try to start immediately (will work if user already clicked)
    _audio.pause();
    _audio.currentTime = 0;
    _audio.src = _wantSrc;
    _audio.loop = _wantLoop;
    _audio.play().catch(() => {
      // Play blocked — that's fine, ensureMusicPlaying() will
      // be called on the next user click via App.tsx onClick.
    });
  }, [desiredContext]);

  // ── Advance battle playlist when track ends naturally ──────
  useEffect(() => {
    const onEnded = () => {
      if (prevContextRef.current !== 'BATTLE') return;

      const list = battlePlaylist.current;
      const next = battleIndex.current + 1;

      if (next < list.length) {
        battleIndex.current = next;
      } else {
        battlePlaylist.current = shuffleArray(BATTLE_TRACKS);
        battleIndex.current = 0;
      }

      const track = battlePlaylist.current[battleIndex.current];
      _wantSrc = track;
      _wantLoop = false;
      _audio.src = track;
      _audio.loop = false;
      _audio.play().catch(() => {});
    };

    _audio.addEventListener('ended', onEnded);
    return () => _audio.removeEventListener('ended', onEnded);
  }, []);

  // ── Volume sync ────────────────────────────────────────────
  useEffect(() => {
    _audio.muted = globalMute || musicVolume === 0;
    _audio.volume = Math.max(0, Math.min(100, musicVolume)) / 100;
  }, [musicVolume, globalMute]);

  return null; // No DOM output
};

export default AudioPlayer;
